// Copyright 2018-2019 The MathWorks, Inc.
/**
 * Defines an API for logging product usage
 *
 * ********************************************************************************************************************
 *
 * IMPORTANT
 *
 * Before enabling usage logging for a product or adding to existing usage logging you must consult with the
 * Data Driven User Design (DDUX) Team.  This is important because they are responsible for data management
 * and related provisioning.  Their responsibilities also include assessing the cost/benefit of additional data
 * streams.
 *
 * ********************************************************************************************************************
 */
define(["dojo/_base/declare", "dojo/_base/lang", "dojo/Deferred", "mw-ddux/WidgetLogger", "mw-log/Log", "mw-messageservice/MessageService", "mw-utils/Utils"], function (declare, lang, Deferred, WidgetLogger, Log, MessageService, Utils) {

    var _started;
    var _uiEventDefaults;
    var _telemetryDefaults;
    var _compositionDefaults;
    var _channel;
    var _responseChannel;
    var _responseSequence = 0;
    var _awaitingResponses = [];
    var _elementIdPrefixes = {};
    var _taps = [];
    var _selectedContext;

    var USAGE_SETUP_CHANNEL = "/UsageSetup";
    var USAGE_DATA_CHANNEL = "/UsageData";
    var USAGE_RESPONSE_CHANNEL = "/UsageResponse";

    return {
        /**
         * Initializes usage logging.  Data logging calls made before start or after end are dropped.
         *
         * @param defaultProperties {object} supplies default values for properties that are common to
         *        all UI events and/or all telemetry logging
         * @param defaultProperties.product {string} identifies the product in all UI event and
         *        telemetry logs unless overriden
         * @param defaultProperties.scope {string} identifies the scope in all UI event logs
         *        unless overridden.
         * @param defaultProperties.appComponent {string} identifies the appComponent in all
         *        telemetry logs unless overridden.  "scope" and "appComponent" play a similar roll
         *        in UI event logs and telemetry logs, respecively.  If the same value should be used
         *        for both you may specify either rather than both.
         * @param defaultProperties.eventKey {string} identifies the telemetry stream in all
         *        telemetry logs unless overridden
         * @param defaultProperties.artifactType {string} identifies the type of artifact associated with all
         *        artifact composition logs unless overridden.
         * @param defualtProperties.allowProductScopeOverride {boolean} should be set true if
         *        products and scopes other than those specified above can exist in the same window.
         *        In this case the UsageLogger will look for annotations on the DOM that can be used
         *        to override the default product and scope.
         *
         * The following deprecated call signature is also supported for backward compatibility.
         *
         * start: function(product, scope, allowProductScopeOverride)
         */
        start: function start(defaultProperties, legacyParameter1, legacyParameter2) {
            if (_started) {
                return;
            }
            // Type check first argument to see if deprecated signature is being used
            if (typeof defaultProperties === "string") {
                _uiEventDefaults = {
                    product: defaultProperties,
                    scope: legacyParameter1,
                    allowProductScopeOverride: legacyParameter2
                };
                _telemetryDefaults = {
                    product: defaultProperties,
                    appComponent: legacyParameter1
                };
                _compositionDefaults = {
                    product: defaultProperties,
                    appComponent: legacyParameter1
                };
            } else {
                _uiEventDefaults = {
                    product: defaultProperties.product,
                    scope: defaultProperties.scope || defaultProperties.appComponent,
                    allowProductScopeOverride: defaultProperties.allowProductScopeOverride
                };
                _telemetryDefaults = {
                    product: defaultProperties.product,
                    appComponent: defaultProperties.appComponent || defaultProperties.scope,
                    eventKey: defaultProperties.eventKey
                };
                _compositionDefaults = {
                    product: defaultProperties.product,
                    appComponent: defaultProperties.appComponent || defaultProperties.scope,
                    artifactType: defaultProperties.artifactType,
                    artifactId: defaultProperties.artifactId
                };
            }
            WidgetLogger.setup(_uiEventDefaults.allowProductScopeOverride, lang.hitch(this, "logEvent"));
            _started = true;
            _channel = USAGE_DATA_CHANNEL + "/" + Utils.generateUuid();
            _responseChannel = USAGE_RESPONSE_CHANNEL + "/" + Utils.generateUuid();
            MessageService.publish(USAGE_SETUP_CHANNEL, {
                type: "start",
                product: _uiEventDefaults.product,
                scope: _uiEventDefaults.scope,
                channel: _channel,
                responseChannel: _responseChannel
            });
            MessageService.subscribe(_responseChannel, "_handleResponse", this);
        },

        /**
         * Terminated usage logging.
         */
        stop: function stop() {
            if (_started) {
                MessageService.publish(USAGE_SETUP_CHANNEL, {
                    type: "stop",
                    channel: _channel,
                    responseChannel: _responseChannel
                });
                MessageService.unsubscribe(_responseChannel, "_handleResponse", this);
                _started = false;
            }
        },

        isRunning: function isRunning() {
            return _started;
        },

        /**
         * Logs a UI event
         *
         * @param eventData {eventType: string, elementId: string, elementType: string}
         *                          the event data must include the first two of the above properties but may also include any
         *                          number of additional string-valued properties.
         * @param unused
         * @param immediate {boolean} if true the event will be logged immediately.  Otherwise logging may be deferred.
         */
        logEvent: function logEvent(eventData, unused, immediate) {
            if (_started) {
                Log.assert(eventData.eventType, "eventType must be defined");
                if (eventData.elementType) {
                    if (!eventData.elementId) {
                        Log.warn("Can't log " + eventData.elementType + " " + eventData.eventType + " without elementId");
                        return;
                    }
                }
                immediate = immediate || unused;
                eventData.product = _uiEventDefaults.product;
                eventData.scope = _uiEventDefaults.scope;
                var that = this;
                var doLogEvent = function doLogEvent() {
                    var attributes;
                    try {
                        that._filterElementId(eventData);
                        // Include the selected context if the event is a keypress or a toolstrip event
                        if (_selectedContext && (eventData.eventType === "keypress" || attributes && attributes.subscope === "toolstrip")) {
                            eventData.selection = _selectedContext;
                        }
                        eventData.messageType = "uiEvent";
                        that._sendLogMessage(eventData);
                    } catch (error) {
                        Log.warn("Error while logging event: " + error.message);
                    }
                };
                // Defer activity to avoid potential adverse affect on responsiveness unless instructed otherwise
                if (immediate) {
                    doLogEvent();
                } else {
                    setTimeout(doLogEvent, 0);
                }
            }
        },

        /**
         * Specifies a context that may be helpful in interpreting keyboard or toolstrip UI events.
         * For example the caller could provide the id associated with the selected region of the UI.
         * This id will be included in subsequent event logs as the "selected" field when relevant.
         * @param contextid
         */
        setSelectedContext: function setSelectedContext(context) {
            _selectedContext = context;
        },

        setPrefixesOfElementIdsToRedact: function setPrefixesOfElementIdsToRedact(prefixes, product, scope) {
            _elementIdPrefixes[this._productAndScopeKey(product || _uiEventDefaults.product, scope || _uiEventDefaults.scope)] = prefixes;
        },

        /**
         * @deprecated call WidgetLogger method directly
         */
        enableLoggingFromNode: function enableLoggingFromNode(node, subscope) {
            WidgetLogger.enableLoggingFromNode(node, subscope);
        },

        /**
         * @param data {object} abitrary key value pairs to be logged
         * @param identification {object} optional properties that identify the data source, overriding the
         *        defaults specified via start
         * @param identification.product
         * @param identification.appComponent
         * @param identification.eventKey
         * @param options {object} optional handling instructions
         * @param options.verifyParametersOnly {boolean} if true, data will not actually be sent
         */
        logData: function logData(data, identification, options) {
            this._sendLogMessage(this._composeTelemetryMessage(data, identification, options));
        },

        /**
         * Similar to logData but returns a promise for a result.  The result will resolve to one of
         * the following values:
         *
         * 0 - SUCCESS
         * 1 - NOT_SUPPORTED
         * 2 - DDUX_NOT_ENABLED
         * 3 - REQUIRED_PROPERTY_BLANK
         * 4 - KEY_VALUES_EXCEED_LIMIT
         * 5 - MISSING_KEY_VALUE_PAIR
         * 6 - EXCEPTION_WAS_THROWN
         * 7 - EVENT_KEY_DISABLED
         * 8 - MESSAGE_TOO_BIG
         */
        logDataObtainResult: function logDataObtainResult(data, identification, options) {
            var message = this._composeTelemetryMessage(data, identification, options);
            message.responseSequence = _responseSequence++;
            var deferred = new Deferred();
            deferred.responseSequence = message.responseSequence;
            _awaitingResponses.push(deferred);
            this._sendLogMessage(message);
            return deferred.promise;
        },

        /**
         * @param identification {object} properties that identify the data source and artifact, overriding the
         *        defaults, if any, specified via start
         * @param identification.product {string}
         * @param identification.appComponent {string}
         * @param identification.artifactType {string}
         * @param identification.artifactId {string} uniquely identifies the artifact.  This string will be hashed so that any
         *        contained IP or PII will be obscured.
         * @param properties {object} artifact properties names and values to be logged.  The values may be string, boolean or number
         * @param componentCounts {object} artifact component types and corresponding non-zero counts
         */
        logComposition: function logComposition(identification, properties, componentCounts) {
            identification = identification || {};
            // Compose message
            var message = {
                messageType: "composition",
                product: identification.product || _compositionDefaults.product,
                appComponent: identification.appComponent || _compositionDefaults.appComponent,
                artifactType: identification.artifactType || _compositionDefaults.artifactType,
                artifactId: identification.artifactId || _compositionDefaults.artifactId,
                properties: properties,
                componentCounts: componentCounts
            };
            Log.assert(message.product, "product must be defined");
            Log.assert(message.appComponent, "appComponent must be defined");
            Log.assert(message.artifactType, "artifactType must be defined");
            Log.assert(message.artifactId, "artifactId must be defined");
            this._sendLogMessage(message);
        },

        /**
         * Supports "taping" the ddux log stream for test.
         *
         * @param matchProperties {object} determines which ddux logs are sent to the given tapFunction.
         *        matchProperties can contain any set of properties and values (including empty) that may
         *        appear in the ddux logs.  Logs matching all the specified properties will be passed to the
         *        tapFunction.  If matchProperties is empty then all logs will be passed.  An example
         *        matchProperties value might be {product: "Image Processing Toolbox", scope: "Camera Calibrator"}.
         * @param tapFunction {function (object)} function to which matching logs will be passed.
         * @param blockDefault {boolean} if true then data sent to the tap function will not be send on down the pipe.
         * @return an object whose remove() function can be called to remove the tap
         */
        addTap: function addTap(matchProperties, tapFunction, blockDefault) {
            var tap = {
                matchProperties: matchProperties,
                tapFunction: tapFunction,
                blockDefault: blockDefault
            };
            _taps.push(tap);
            return { remove: function remove() {
                    var index = _taps.indexOf(tap);
                    if (index >= 0) {
                        _taps.splice(index, 1);
                    }
                } };
        },

        /**
         * Removes a tap that was added via the addTap method.
         *
         * @param matchProperties {object} the matchProperties that were passed to the addTap call
         * @param tapFunction {function} the function that was passed to the addTap call
         *
         * @deprecated call remove() on the object returned by addTap instead.
         */
        removeTap: function removeTap(matchProperties, tapFunction) {
            for (var i = 0; i < _taps.length; i++) {
                var tap = _taps[i];
                if (tap.tapFunction === tapFunction) {
                    var keys1 = Object.keys(matchProperties);
                    var keys2 = Object.keys(tap.matchProperties);
                    var allMatch = keys1.length === keys2.length;
                    for (var j = 0; j < keys1.legnth && allMatch; j++) {
                        var key = keys1[j];
                        allMatch = matchProperties[key] === tap.matchProperties[key];
                    }
                    if (allMatch) {
                        _taps.splice(i, 1);
                        i--;
                    }
                }
            }
        },

        _filterElementId: function _filterElementId(eventData) {
            var product = eventData.product || _uiEventDefaults.product;
            var scope = eventData.scope || _uiEventDefaults.scope;
            var prefixes = _elementIdPrefixes[this._productAndScopeKey(product, scope)];
            var isNumber = function isNumber(s) {
                return !/\D/.test(s);
            };
            if (prefixes) {
                var i, prefix;
                for (i = 0; i < prefixes.length; i++) {
                    prefix = prefixes[i];
                    if (eventData.elementId.startsWith(prefix)) {
                        var remainder = eventData.elementId.slice(prefix.length);
                        eventData.elementId = prefix + ":redacted";
                        if (remainder.length > 0) {
                            var c = remainder.charAt(0);
                            if (c === ":" || c === ".") {
                                eventData.redactedHash = this.hashString(remainder);
                            } else if (isNumber(remainder)) {
                                eventData.redactedHash = remainder;
                            }
                        }
                        break;
                    }
                }
            }
        },

        _productAndScopeKey: function _productAndScopeKey(product, scope) {
            return product + ":" + scope;
        },

        // TODO: replace with something standard like MD5.
        hashString: function hashString(s) {
            var hash = 0;
            for (var i = 0; i < s.length; i++) {
                hash += Math.pow(s.charCodeAt(i) * 31, s.length - i);
                hash &= hash;
            }
            return Math.abs(hash).toString(16);
        },

        _composeTelemetryMessage: function _composeTelemetryMessage(data, identification, options) {
            identification = identification || {};
            var message = {
                messageType: "telemetry",
                product: identification.product || _telemetryDefaults.product,
                appComponent: identification.appComponent || _telemetryDefaults.appComponent,
                eventKey: identification.eventKey || _telemetryDefaults.eventKey,
                data: data
            };
            if (options) {
                message.verifyParametersOnly = options.verifyParametersOnly;
            }
            Log.assert(message.product, "product must be defined");
            Log.assert(message.appComponent, "appComponent must be defined");
            Log.assert(message.eventKey, "eventKey must be defined");
            return message;
        },

        _sendLogMessage: function _sendLogMessage(message) {
            // Send to matching taps
            var blocked = false;
            _taps.forEach(function (tap) {
                var matchKeys = Object.keys(tap.matchProperties);
                var allMatch = true;
                for (var k = 0; k < matchKeys.length && allMatch; k++) {
                    var key = matchKeys[k];
                    allMatch = tap.matchProperties[key] === message[key];
                }
                if (allMatch) {
                    tap.tapFunction(message);
                    if (tap.blockDefault) {
                        blocked = true;
                    }
                }
            });
            if (!blocked) {
                MessageService.publish(_channel, message);
            }
        },

        _handleResponse: function _handleResponse(message) {
            var sequence = message.data.responseSequence;
            while (_awaitingResponses.length > 0) {
                var awaitingResponse = _awaitingResponses.shift();
                if (awaitingResponse.responseSequence === sequence) {
                    if (message.data.messageType === "telemetryStatus") {
                        awaitingResponse.resolve(message.data.status);
                    }
                    break;
                } else {
                    awaitingResponse.reject();
                }
            }
        }
    };
});

